import { useApolloClient, useLazyQuery, useQuery } from '@apollo/client';
import * as Types from '@openx/types/gqlTypes.ts';
import type { UsePackagesListFetch } from '@openx/types/package/PackageHookTypes.ts';
import type { Package } from '@openx/types/package.ts';
import { getPackageListTargetingValues } from '@openx/utils/lib/packages/getPackageListTargetingValues.ts';
import { OPEN_AUDIENCE_ORG_NAME } from '@openx/utils/services/config.ts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { useAuth } from '../../../components/AuthContext/context.ts';
import { TABLE_PAGE_SIZE } from '../../../config.ts';
import {
  GetOpenxCuratedPackagesDocument,
  type GetOpenxCuratedPackagesQuery,
} from '../../../graphql/query/openxCuratedPackages/openxCuratedPackages.generated.tsx';
import { GetPackageListDocument } from '../../../graphql/query/packages/GetPackages.generated.tsx';
import { defaultCriteria } from '../../../modules/inventory/packages/PackagesList/constants.ts';
import { findLastPopulatedPage } from '../../../utils/findLastPopulatedPage.ts';
import { useLastPopulatedPageRedirect } from '../../../utils/useLastPopulatedPageRedirect.ts';

const emptyCriteriaObject = {
  criteria: defaultCriteria,
  criteriaGql: {
    limit: TABLE_PAGE_SIZE,
    order_by: [{ name: Types.Order_By.Desc }],
  },
};

export const usePackagesListFetch: UsePackagesListFetch = (criteriaObjectOa = emptyCriteriaObject) => {
  const { criteria, criteriaGql, accountNetworkId, onPageNumberCorrection } = criteriaObjectOa;
  const { pagination = defaultCriteria.pagination } = criteria || {};

  const apolloClient = useApolloClient();
  const { session } = useAuth();

  const isOaSuborg = session?.account?.name !== OPEN_AUDIENCE_ORG_NAME;

  const location = useLocation();
  const isCurated = location.pathname.includes('curated');
  const isCuratedPackagesQuery = isCurated && isOaSuborg;
  const isCuratedListFilter = isCurated && !isOaSuborg;

  const searchPhrase = (criteria?.filters?.phrase ?? '').toLowerCase();
  const accountUids = criteria?.filters?.account_uid;
  const adPlacement = typeof criteria?.filters?.ad_placement === 'string' ? criteria.filters.ad_placement : undefined;
  const distributionChannel =
    typeof criteria?.filters?.distribution_channel === 'string' ? criteria.filters.distribution_channel : undefined;
  const deviceType = typeof criteria?.filters?.device_type === 'string' ? criteria.filters.device_type : undefined;

  const adPlacements = useMemo(
    () =>
      adPlacement
        ?.split(',')
        .map(value => value.trim())
        .filter(Boolean) ?? [],
    [adPlacement]
  );
  const distributionChannels = useMemo(
    () =>
      distributionChannel
        ?.split(',')
        .map(value => value.trim())
        .filter(Boolean) ?? [],
    [distributionChannel]
  );
  const deviceTypes = useMemo(
    () =>
      deviceType
        ?.split(',')
        .map(value => value.trim())
        .filter(Boolean) ?? [],
    [deviceType]
  );

  const pageNumber = pagination.pageNumber ?? 1;
  const limit = pagination.pageSize ?? TABLE_PAGE_SIZE;
  const offset = (pageNumber - 1) * limit;

  const queryString = useMemo(() => {
    const params = new URLSearchParams();

    params.set('limit', String(limit));

    if (offset > 0) {
      params.set('offset', String(offset));
    }

    if (accountNetworkId) {
      params.set('exchange_account_network_id', accountNetworkId);
    }

    if (isCuratedListFilter) {
      params.set('is_curated', 'true');
    }

    const orderBy = criteriaGql?.order_by;

    if (Array.isArray(orderBy) && orderBy.length > 0) {
      const firstSort = orderBy[0];
      const [column, direction] = Object.entries(firstSort)[0];
      const prefix = direction?.toString().startsWith('desc') ? '-' : '';

      params.set('sort', `${prefix}${column}`);
    }

    if (searchPhrase) params.set('q', searchPhrase);
    if (accountUids?.length) params.set('account_uid', accountUids.join(','));
    if (adPlacement) params.set('ad_placement', adPlacement);
    if (distributionChannel) params.set('distribution_channel', distributionChannel);
    if (deviceType) params.set('device_type', deviceType);

    return params.toString();
  }, [
    limit,
    offset,
    criteriaGql,
    accountNetworkId,
    isCuratedListFilter,
    searchPhrase,
    accountUids,
    adPlacement,
    distributionChannel,
    deviceType,
  ]);

  const [fetchCuratedPackages, { loading: curatedQueryLoading }] = useLazyQuery(GetOpenxCuratedPackagesDocument);
  const [curatedData, setCuratedData] = useState<GetOpenxCuratedPackagesQuery | null>(null);

  const curatedLoading = curatedQueryLoading && !curatedData;

  useEffect(() => {
    if (isCuratedPackagesQuery && !curatedData) {
      fetchCuratedPackages().then(result => {
        if (result.data) {
          setCuratedData(result.data);
        }
      });
    }
  }, [isCuratedPackagesQuery, curatedData, fetchCuratedPackages]);

  const { data: packageListData, loading: packageListLoading } = useQuery(GetPackageListDocument, {
    skip: isCuratedPackagesQuery,
    variables: { queryString },
  });

  const allCuratedPackages: Package[] = useMemo(
    () =>
      curatedData?.openxCuratedPackages?.reduce((acc, item) => {
        if (item?.obj) {
          acc.push(item.obj);
        }

        return acc;
      }, [] as Package[]) ?? [],
    [curatedData?.openxCuratedPackages]
  );

  const filteredCuratedPackages: Package[] = useMemo(
    () =>
      allCuratedPackages.filter(pkg => {
        const matchesSearch =
          !searchPhrase || pkg.name.toLowerCase().includes(searchPhrase) || pkg.id.toLowerCase().includes(searchPhrase);
        const targetingValues = getPackageListTargetingValues(pkg.targeting);
        const packageAdPlacements =
          targetingValues.ad_placement
            ?.split(',')
            .map(value => value.trim())
            .filter(Boolean) ?? [];
        const packageDistributionChannels =
          targetingValues.distribution_channel
            ?.split(',')
            .map(value => value.trim())
            .filter(Boolean) ?? [];
        const packageDeviceTypes =
          targetingValues.device_type
            ?.split(',')
            .map(value => value.trim())
            .filter(Boolean) ?? [];

        const matchesAdPlacement =
          adPlacements.length === 0 || adPlacements.some(value => packageAdPlacements.includes(value));
        const matchesDistributionChannel =
          distributionChannels.length === 0 ||
          distributionChannels.some(value => packageDistributionChannels.includes(value));
        const matchesDeviceType =
          deviceTypes.length === 0 || deviceTypes.some(value => packageDeviceTypes.includes(value));

        return matchesSearch && matchesAdPlacement && matchesDistributionChannel && matchesDeviceType;
      }),
    [adPlacements, allCuratedPackages, deviceTypes, distributionChannels, searchPhrase]
  );

  const sortedCuratedPackages: Package[] = useMemo(() => {
    const orderBy = criteriaGql?.order_by;
    const nameSortEntry = Array.isArray(orderBy)
      ? orderBy.find((entry: Record<string, unknown>) => 'name' in entry)
      : undefined;

    const nameSortDirection = (nameSortEntry as { name?: string } | undefined)?.name ?? '';
    const isAsc = nameSortDirection.startsWith('asc');

    return [...filteredCuratedPackages].sort((a, b) => {
      const comparison = a.name.localeCompare(b.name);

      return isAsc ? comparison : -comparison;
    });
  }, [filteredCuratedPackages, criteriaGql?.order_by]);

  const regularPackageData = packageListData?.packages;
  const regularPackageResponseLoaded = regularPackageData !== undefined;
  const regularPackageObjectsCount = regularPackageData?.objects?.length ?? 0;
  const regularPackagesHasMore = regularPackageResponseLoaded ? (regularPackageData.has_more ?? false) : undefined;

  const regularPackages: Package[] = useMemo(
    () =>
      regularPackageData?.objects?.reduce((acc, item) => {
        if (item?.obj) {
          acc.push(item.obj);
        }

        return acc;
      }, [] as Package[]) ?? [],
    [regularPackageData?.objects]
  );

  const resolveLastPackagePage = useCallback(
    (outOfRangePage: number, abortSignal?: AbortSignal) =>
      findLastPopulatedPage({
        requestedPage: outOfRangePage,
        abortSignal,

        isPagePopulated: async (candidatePage, signal) => {
          const probeParams = new URLSearchParams(queryString);
          const probeOffset = (candidatePage - 1) * limit;

          probeParams.set('limit', '1');

          if (probeOffset > 0) {
            probeParams.set('offset', String(probeOffset));
          } else {
            probeParams.delete('offset');
          }

          const { data } = await apolloClient.query({
            query: GetPackageListDocument,
            variables: {
              queryString: probeParams.toString(),
            },
            fetchPolicy: 'no-cache',
            context: {
              fetchOptions: {
                signal,
              },
            },
          });

          return (data.packages?.objects?.length ?? 0) > 0;
        },
      }),
    [apolloClient, limit, queryString]
  );

  const redirectToLastPackagePage = useCallback(
    (lastPopulatedPage: number, outOfRangePage: number) => {
      if (pageNumber === outOfRangePage) {
        onPageNumberCorrection?.(lastPopulatedPage);
      }
    },
    [onPageNumberCorrection, pageNumber]
  );

  useLastPopulatedPageRedirect({
    enabled: !isCuratedPackagesQuery && regularPackageResponseLoaded && Boolean(onPageNumberCorrection),
    requestedPage: pageNumber,
    loading: packageListLoading,
    objectsLength: regularPackageObjectsCount,
    hasMore: regularPackagesHasMore,
    resolveLastPopulatedPage: resolveLastPackagePage,
    onRedirect: redirectToLastPackagePage,
  });

  const packagesList: Package[] = useMemo(() => {
    if (isCuratedPackagesQuery) {
      const startIndex = (pageNumber - 1) * limit;

      return sortedCuratedPackages.slice(startIndex, startIndex + limit);
    }

    return regularPackages;
  }, [isCuratedPackagesQuery, sortedCuratedPackages, regularPackages, pageNumber, limit]);

  const loading = isCuratedPackagesQuery ? curatedLoading : packageListLoading;

  const totalCount = isCuratedPackagesQuery ? filteredCuratedPackages.length : regularPackageObjectsCount + offset;

  // Curated packages are fetched in full and paginated locally,
  // so their totalCount is exact and can safely clamp page-number input.
  const hasMore = isCuratedPackagesQuery ? false : regularPackagesHasMore;

  const updatedCriteria = useMemo(
    () => ({
      ...criteria,
      pagination: {
        ...pagination,
        totalCount,
        ...(hasMore !== undefined && { hasMore }),
      },
    }),
    [criteria, pagination, totalCount, hasMore]
  );

  return {
    criteria: updatedCriteria,
    loading,
    packagesList,
    selectedData: [],
  };
};
